import { useEffect, useState } from 'react';
import { Table, DatePicker } from 'antd';
import type { Dayjs } from 'dayjs';
import QRCode from 'qrcode';
import { I } from './icons';
import type { InvitesProps, Row, AdminSale, AdminAccrual } from './types';

const { RangePicker } = DatePicker;

const SEND_URL = '/invite/send';
const REGISTER_URL = '/user/register';
const LOGIN_URL = '/user/security/login';

function money(v: number): string {
    return v.toLocaleString('ru-RU') + ' ₽';
}

/* Дата с сервера в формате DD.MM.YYYY → числовой ключ YYYYMMDD (для сравнения диапазона). */
function dkey(d: string): number {
    const p = (d || '').split('.');
    return p.length === 3 ? +(p[2] + p[1] + p[0]) : 0;
}
type Range = [Dayjs | null, Dayjs | null] | null;
/* Фильтр строк админ-таблицы по выбранному диапазону дат (поле field содержит DD.MM.YYYY). */
function inRange<T>(items: T[], range: Range, field: keyof T): T[] {
    if (!range || !range[0] || !range[1]) return items;
    const lo = +range[0].format('YYYYMMDD');
    const hi = +range[1].format('YYYYMMDD');
    return items.filter(x => { const k = dkey(String(x[field] ?? '')); return k >= lo && k <= hi; });
}

/* ── Настоящий сканируемый QR реф-ссылки (qrcode → data-URL) ── */
function QR({ value }: { value: string }) {
    const [src, setSrc] = useState('');
    useEffect(() => {
        let alive = true;
        QRCode.toDataURL(value, { margin: 1, width: 264 })
            .then((u) => { if (alive) setSrc(u); })
            .catch(() => { if (alive) setSrc(''); });
        return () => { alive = false; };
    }, [value]);
    return src
        ? <img className="qr" src={src} width={132} height={132} alt="QR-код реферальной ссылки" />
        : <div className="qr" />;
}

const RATES = [
    { icon: 'car' as const, color: '#b40c2e', bg: '#fff1f4', prod: 'ОСАГО', sub: 'За каждый оплаченный полис', val: '100 ₽', cap: 'за полис · но не более 10% от КВ' },
    { icon: 'house' as const, color: '#207732', bg: '#e8f5ec', prod: 'Ипотечное страхование', sub: 'От вашего комиссионного вознаграждения', val: '10%', cap: 'от КВ реферала' },
];

function Faq() {
    const items = [
        { q: 'Кто считается моим рефералом?', a: 'Реферал — это новый агент, который зарегистрировался в AgentBroker по вашей персональной ссылке и ранее не был зарегистрирован в системе. Программа действует только для новых агентов и только по одному уровню — вознаграждение начисляется с продаж самого реферала, но не с продаж тех, кого пригласит он.' },
        { q: 'Когда начисляется вознаграждение?', a: 'Вознаграждение начисляется только с оплаченных продаж реферала. Как только полис вашего реферала оплачен и КВ подтверждено, начисление отражается в этой таблице и попадает в ваш баланс к ближайшей выплате.' },
        { q: 'Сколько действует привязка реферала?', a: 'Привязка действует 12 месяцев с даты регистрации реферала. В течение этого срока вы получаете вознаграждение со всех его оплаченных продаж. После окончания срока новые начисления по этому агенту прекращаются.' },
        { q: 'Можно ли пригласить агента, который уже был в AgentBroker?', a: 'Нет. Программа действует только для новых агентов. При регистрации по вашей ссылке и в дальнейшем мы автоматически сверяем данные приглашённого со всей базой агентов по паспорту, ИНН, СНИЛС, номеру телефона и email. Если находится ранее зарегистрированный агент с такими же реквизитами, приглашённый считается не новым — вознаграждение по нему не начисляется, а уже созданные начисления удерживаются. Проверка повторяется и позже: реквизиты часто заполняются через недели после регистрации, и как только совпадение обнаруживается, привязка помечается как не новая. Приглашайте только тех, кто ещё ни разу не регистрировался на сервисе.' },
        { q: 'Почему по агенту нет начисления?', a: 'Начисление не появится, если: агент зарегистрировался не по вашей ссылке; полис оформил не он сам; он уже был агентом AgentBroker раньше; полис ещё не оплачен (или был отменён/возвращён); либо истёк 12-месячный срок привязки. Пока реферал не оформил и не оплатил первую продажу, он в статусе «Ждёт первой продажи».' },
    ];
    const [open, setOpen] = useState(0);
    return (
        <>
            {items.map((f, i) => (
                <div key={i} className={'faq-item' + (open === i ? ' open' : '')}>
                    <button className="faq-q" onClick={() => setOpen(open === i ? -1 : i)}>
                        {f.q}<I.plus />
                    </button>
                    <div className="faq-a"><div className="faq-a-inner">{f.a}</div></div>
                </div>
            ))}
        </>
    );
}

export function InvitesPage({ inviteUrl, csrf, initial }: InvitesProps) {
    const IS_GUEST = !!initial.isGuest;
    const IS_ADMIN = !!initial.isAdmin;

    // F5: фильтр админ-таблиц по датам (клиентский, по колонке даты).
    const [salesRange, setSalesRange] = useState<Range>(null);
    const [accrualsRange, setAccrualsRange] = useState<Range>(null);

    const [copied, setCopied] = useState(false);
    // inviteUrl приходит с сервера уже с полной схемой (http/https). Схему НЕ добавляем повторно —
    // иначе выходит http://http://… . Fallback-префикс — только если схемы вдруг нет (старый payload).
    const fullLink = () => /^https?:\/\//i.test(inviteUrl) ? inviteUrl : location.protocol + '//' + inviteUrl;
    const copy = () => {
        try { navigator.clipboard.writeText(fullLink()); } catch { /* noop */ }
        setCopied(true); setTimeout(() => setCopied(false), 1800);
    };
    // Поделиться персональной ссылкой в мессенджерах.
    const share = (net: 'wa' | 'tg' | 'vk') => {
        const u = encodeURIComponent(fullLink());
        const t = encodeURIComponent('Присоединяйтесь к AgentBroker по моей ссылке — оформляйте страховки онлайн с выгодным вознаграждением');
        const links: Record<string, string> = {
            wa: 'https://wa.me/?text=' + t + '%20' + u,
            tg: 'https://t.me/share/url?url=' + u + '&text=' + t,
            vk: 'https://vk.com/share.php?url=' + u,
        };
        if (links[net]) window.open(links[net], '_blank', 'noopener,noreferrer');
    };

    // Приглашение по email (реальная отправка на /invite/send)
    const [email, setEmail] = useState('');
    const [emailState, setEmailState] = useState<'idle' | 'error' | 'sent'>('idle');
    const [emailMsg, setEmailMsg] = useState('');
    const [sending, setSending] = useState(false);
    const [rows, setRows] = useState<Row[]>(initial.rows || []);
    const [sentCount, setSentCount] = useState(initial.sentTotal || 0);
    // Новая отправка встаёт сверху группы «отправленные», но НИЖЕ принятых рефералов.
    const addSentRow = (row: Row) => setRows(rs => {
        const refs = rs.filter(r => r.kind === 'referral');
        const sents = rs.filter(r => r.kind === 'sent');
        return [...refs, row, ...sents];
    });
    const sendInvite = async () => {
        const addr = email.trim();
        const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addr);
        if (!ok) { setEmailState('error'); setEmailMsg('Проверьте адрес — кажется, в нём ошибка'); return; }
        if (sending) return;
        setSending(true); setEmailState('idle'); setEmailMsg('');
        try {
            const headers: Record<string, string> = { 'Content-Type': 'application/json', 'Accept': 'application/json' };
            if (csrf) headers['X-CSRF-Token'] = csrf;
            const res = await fetch(SEND_URL, {
                method: 'POST',
                credentials: 'same-origin',
                headers,
                body: JSON.stringify({ email: addr }),
            });
            const data = await res.json().catch(() => ({}));
            if (res.ok && data.ok) {
                setEmailState('sent'); setEmailMsg(data.message || 'Письмо с вашей ссылкой отправлено');
                if (data.row) { addSentRow(data.row); setSentCount(c => c + 1); }
                setEmail('');
                setTimeout(() => { setEmailState('idle'); setEmailMsg(''); }, 2800);
            } else {
                setEmailState('error'); setEmailMsg((data && data.message) || 'Не удалось отправить приглашение');
                if (data && data.row) { addSentRow(data.row); setSentCount(c => c + 1); }
            }
        } catch {
            setEmailState('error'); setEmailMsg('Сеть недоступна — попробуйте ещё раз');
        } finally {
            setSending(false);
        }
    };

    // Calculator
    const [agents, setAgents] = useState(5);
    const [osago, setOsago] = useState(20);
    const [ipoteka, setIpoteka] = useState(2);
    const IPO_KV = 6000;
    const monthOsago = agents * osago * 100;
    const monthIpo = agents * ipoteka * IPO_KV * 0.10;
    const monthTotal = monthOsago + monthIpo;
    const yearTotal = monthTotal * 12;

    const totalEarned = rows.filter(r => r.kind === 'referral').reduce((s, r) => s + (r.earned || 0), 0);

    const heroMetrics = (initial.hero || []).map(m => m.k === 'Отправлено приглашений' ? { ...m, v: String(sentCount) } : m);

    const scrollTo = (id: string) => {
        const el = document.getElementById(id);
        if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 80, behavior: 'smooth' });
    };

    const chipTone = (tone?: string) => ({
        green: { bg: 'var(--ab-color-success-soft)', fg: 'var(--ab-color-success-strong)', dot: 'var(--ab-color-success)' },
        orange: { bg: 'var(--ab-color-warning-soft)', fg: 'var(--ab-color-warning-strong)', dot: 'var(--ab-color-warning)' },
        blue: { bg: 'var(--ab-color-brand-50)', fg: 'var(--ab-color-brand-600)', dot: 'var(--ab-color-brand-500)' },
        red: { bg: '#fde8e8', fg: '#dc2626', dot: '#dc2626' },
        muted: { bg: 'var(--ab-color-bg-muted)', fg: 'var(--ab-color-text-muted)', dot: 'var(--ab-color-text-subtle)' },
    }[tone || 'muted'] || { bg: 'var(--ab-color-bg-muted)', fg: 'var(--ab-color-text-muted)', dot: 'var(--ab-color-text-subtle)' });

    const avatarColor = (id: number) => ['#e63946', '#1971c2', '#207732', '#5b21b6', '#d6336c', '#a86104'][Math.abs(id || 0) % 6];
    const dash = <span style={{ color: 'var(--ab-color-text-subtle)' }}>—</span>;

    const refColumn = {
        title: 'Пригласивший', dataIndex: 'referrer', width: 116,
        render: (v: number) => <span style={{ fontFamily: 'var(--ab-font-mono)', fontSize: 13, color: 'var(--ab-color-text-muted)' }}>#{v}</span>,
    };

    const columns: any[] = [
        IS_ADMIN ? refColumn : null,
        {
            title: 'Агент / приглашение', dataIndex: 'name',
            render: (_: unknown, r: Row) => {
                if (r.kind === 'referral') {
                    const initials = (r.name || '?').split(' ').slice(0, 2).map(s => s[0] || '').join('');
                    return <div className="agent-cell"><span className="agent-ava" style={{ background: avatarColor(r.id) }}>{initials}</span><div><div className="nm">{r.name}</div>{r.email ? <div className="contact"><span className="em">{r.email}</span></div> : null}</div></div>;
                }
                return <div className="agent-cell"><span className="agent-ava" style={{ background: 'var(--ab-color-bg-muted)', color: 'var(--ab-color-text-subtle)' }}><I.mail width="15" height="15" /></span><div><div className="nm">{r.email}</div><div className="contact"><span style={{ color: 'var(--ab-color-text-subtle)' }}>ждёт регистрации</span></div></div></div>;
            },
        },
        {
            title: 'Дата', dataIndex: 'date', width: 140,
            render: (_: unknown, r: Row) => <div><div style={{ fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>{r.date}</div><div style={{ fontSize: 11, color: 'var(--ab-color-text-subtle)' }}>{r.dateLabel}</div></div>,
        },
        {
            title: 'Продаж', dataIndex: 'sales', width: 92, align: 'right',
            render: (_: unknown, r: Row) => r.sales == null ? dash : <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>{r.sales}</span>,
        },
        {
            title: 'Заработано', dataIndex: 'earned', width: 130, align: 'right',
            render: (_: unknown, r: Row) => r.earned == null ? dash : <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: r.earned > 0 ? 'var(--ab-color-success-strong)' : 'var(--ab-color-text-subtle)' }}>{money(r.earned)}</span>,
        },
        {
            title: 'Статус', dataIndex: 'chip', width: 220,
            render: (_: unknown, r: Row) => { const c = chipTone(r.chip && r.chip.tone); return <span className="st-chip" style={{ background: c.bg, color: c.fg }}><span className="dot" style={{ background: c.dot }} />{r.chip ? r.chip.label : ''}</span>; },
        },
    ].filter(Boolean);

    return (
        <div className="page-wrap">

            {/* HERO */}
            <div className="hero">
                <div className="hero-inner">
                    <span className="hero-eyebrow"><I.invite width="14" height="14" /> Реферальная программа</span>
                    <h1>Приглашайте агентов — зарабатывайте с их продаж</h1>
                    <p className="lead">Дайте коллеге свою ссылку. Пока он оформляет полисы — вы получаете вознаграждение целый год. Приглашать можно страховых и кредитных агентов, риелторов, вебмастеров.</p>
                    <div className="hero-metrics">
                        {heroMetrics.map((m, i) => (
                            <div className="hero-metric" key={i}><div className="v">{m.v}</div><div className="k">{m.k}</div></div>
                        ))}
                    </div>
                    <div className="hero-cta">
                        {IS_GUEST ? (
                            <>
                                <button className="btn-hero primary" onClick={() => location.href = REGISTER_URL}><I.arrowR width="18" height="18" />Стать агентом и приглашать</button>
                                <button className="btn-hero ghost" onClick={() => location.href = LOGIN_URL}>Войти<I.arrowR width="18" height="18" /></button>
                            </>
                        ) : (
                            <>
                                <button className="btn-hero primary" onClick={() => scrollTo('mylink')}><I.arrowR width="18" height="18" />Скопировать ссылку</button>
                                <button className="btn-hero ghost" onClick={() => scrollTo('invitees')}>Мои приглашённые<I.arrowR width="18" height="18" /></button>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* HOW IT WORKS */}
            <div className="section">
                <div className="section-head">
                    <h2 className="section-title">Как это работает</h2>
                    <p className="section-sub">Три шага — от ссылки до вознаграждения</p>
                </div>
                <div className="steps">
                    <div className="step card">
                        <div className="n">1</div>
                        <h3>Скопируйте ссылку</h3>
                        <p>Возьмите персональную ссылку и отправьте её знакомым агентам или опубликуйте на своей площадке — сайте, блоге, в мессенджере.</p>
                        <I.arrowR className="arrow" />
                    </div>
                    <div className="step card">
                        <div className="n">2</div>
                        <h3>Позовите агента</h3>
                        <p>Новый агент регистрируется по вашей ссылке и появляется в таблице «Мои приглашённые». Дальше мы сопровождаем его сами.</p>
                        <I.arrowR className="arrow" />
                    </div>
                    <div className="step card">
                        <div className="n">3</div>
                        <h3>Получайте 12 месяцев</h3>
                        <p>С каждой оплаченной продажи реферала вам начисляется вознаграждение — в течение 12 месяцев с даты его регистрации.</p>
                    </div>
                </div>
            </div>

            {/* MY LINK */}
            <div className="section" id="mylink">
                {IS_GUEST ? (
                    <div className="link-card" style={{ gridTemplateColumns: '1fr' }}>
                        <div className="lc-body">
                            <div className="lc-title"><I.invite width="18" height="18" style={{ color: 'var(--ab-color-brand-500)' }} /> Ваша персональная ссылка — после регистрации</div>
                            <div className="lc-sub">Зарегистрируйтесь как агент — и получите ссылку вида agentbroker.ru/invite/&lt;ваш ID&gt;, приглашайте коллег по ссылке и по email, зарабатывайте с их продаж 12 месяцев.</div>
                            <div className="hero-cta" style={{ marginTop: 18 }}>
                                <button className="btn-hero primary" onClick={() => location.href = REGISTER_URL}><I.arrowR width="18" height="18" />Зарегистрироваться</button>
                                <button className="btn-hero ghost" style={{ color: 'var(--ab-color-brand-600)', background: 'var(--ab-color-brand-50)', border: '1px solid var(--ab-color-brand-200)' }} onClick={() => location.href = LOGIN_URL}>У меня уже есть аккаунт</button>
                            </div>
                            <div className="lc-new-note"><I.check width="14" height="14" /> Работает только для новых агентов, ещё не зарегистрированных в AgentBroker</div>
                        </div>
                    </div>
                ) : (
                    <div className="link-card">
                        <div className="lc-body">
                            <div className="lc-title"><I.invite width="18" height="18" style={{ color: 'var(--ab-color-brand-500)' }} /> Моя реферальная ссылка</div>
                            <div className="lc-sub">Тот, кто зарегистрируется по этой ссылке, станет вашим рефералом на 12 месяцев.</div>
                            <div className="link-field">
                                <input readOnly value={inviteUrl} onFocus={e => e.currentTarget.select()} />
                                <button className={'copy' + (copied ? ' done' : '')} onClick={copy}>{copied ? <I.check /> : <I.copy />}{copied ? 'Скопировано' : 'Копировать'}</button>
                            </div>
                            <div className="share-row">
                                <span className="lbl">Поделиться:</span>
                                <button className="share-btn wa" onClick={() => share('wa')}><I.wa /> WhatsApp</button>
                                <button className="share-btn tg" onClick={() => share('tg')}><I.tg /> Telegram</button>
                                <button className="share-btn vk" onClick={() => share('vk')}><I.vk /> ВКонтакте</button>
                            </div>
                            <div className="email-invite">
                                <div className="ei-lbl"><I.mail /> Отправить приглашение на почту</div>
                                <div className="ei-row">
                                    <input
                                        type="email"
                                        className={emailState === 'error' ? 'err' : ''}
                                        placeholder="agent@example.ru"
                                        value={email}
                                        onChange={e => { setEmail(e.target.value); if (emailState !== 'idle') setEmailState('idle'); }}
                                        onKeyDown={e => { if (e.key === 'Enter') sendInvite(); }}
                                    />
                                    <button className={'ei-send' + (emailState === 'sent' ? ' done' : '')} onClick={sendInvite} disabled={sending}>
                                        {emailState === 'sent' ? <I.check /> : <I.send />}{sending ? 'Отправляем…' : emailState === 'sent' ? 'Отправлено' : 'Отправить'}
                                    </button>
                                </div>
                                <div className={'ei-hint ' + (emailState === 'error' ? 'err' : emailState === 'sent' ? 'ok' : 'muted')}>
                                    {emailMsg || 'Мы отправим письмо с вашей персональной ссылкой-приглашением'}
                                </div>
                            </div>
                            <div className="lc-new-note"><I.check width="14" height="14" /> Работает только для новых агентов, ещё не зарегистрированных в AgentBroker</div>
                        </div>
                        <div className="qr-box">
                            <QR value={fullLink()} />
                            <span className="qr-cap">Наведите камеру</span>
                        </div>
                    </div>
                )}
            </div>

            {/* RATES */}
            <div className="section">
                <div className="section-head">
                    <h2 className="section-title">Ваше вознаграждение</h2>
                    <p className="section-sub">Начисляется только с оплаченных продаж реферала</p>
                </div>
                <div className="rates">
                    <table>
                        <thead>
                            <tr><th style={{ width: '48%' }}>Продукт реферала</th><th>Ваше вознаграждение</th></tr>
                        </thead>
                        <tbody>
                            {RATES.map((r, i) => {
                                const Ic = I[r.icon];
                                return (
                                    <tr key={i}>
                                        <td>
                                            <div className="prod">
                                                <span className="pic" style={{ background: r.bg, color: r.color }}><Ic /></span>
                                                <div>
                                                    <div>{r.prod}</div>
                                                    <div className="rate-cap">{r.sub}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td><div className="rate-val">{r.val}</div><div className="rate-cap">{r.cap}</div></td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <div className="rates-note"><I.check width="15" height="15" style={{ color: 'var(--ab-color-success)' }} /> <span><span className="pill">Привязка 12 месяцев.</span> Вознаграждение начисляется по одному уровню — только с продаж самого реферала.</span></div>
            </div>

            {/* EARNING EXAMPLES + CALCULATOR */}
            <div className="section">
                <div className="section-head">
                    <h2 className="section-title">Пример заработка</h2>
                    <p className="section-sub">Наглядно и в цифрах — сколько приносит команда рефералов</p>
                </div>
                <div className="ex-grid">
                    <div className="card ex-card">
                        <div className="ex-top">
                            <span className="pic" style={{ background: '#fff1f4', color: '#b40c2e' }}><I.car /></span>
                            <div className="cap">Ваш реферал оформил <b>50 полисов ОСАГО</b> за месяц</div>
                        </div>
                        <div className="ex-flow">
                            <span className="from">50 × 100 ₽</span>
                            <I.arrowR className="arr" />
                            <span className="to">5 000 ₽<small>ваше вознаграждение</small></span>
                        </div>
                    </div>
                    <div className="card ex-card">
                        <div className="ex-top">
                            <span className="pic" style={{ background: '#e8f5ec', color: '#207732' }}><I.house /></span>
                            <div className="cap">Ипотечные полисы реферала с КВ <b>200 000 ₽</b></div>
                        </div>
                        <div className="ex-flow">
                            <span className="from">10% от КВ</span>
                            <I.arrowR className="arr" />
                            <span className="to">20 000 ₽<small>ваше вознаграждение</small></span>
                        </div>
                    </div>
                </div>
                <div className="card calc">
                    <div className="calc-controls">
                        <div className="cc-row">
                            <div className="cc-label"><span>Приглашённых агентов</span><b>{agents}</b></div>
                            <input type="range" min="1" max="30" value={agents} onChange={e => setAgents(+e.target.value)} style={{ width: '100%', accentColor: 'var(--ab-color-brand-500)' }} />
                        </div>
                        <div className="cc-row">
                            <div className="cc-label"><span>Полисов ОСАГО на агента / мес</span><b>{osago}</b></div>
                            <input type="range" min="0" max="80" value={osago} onChange={e => setOsago(+e.target.value)} style={{ width: '100%', accentColor: 'var(--ab-color-brand-500)' }} />
                        </div>
                        <div className="cc-row">
                            <div className="cc-label"><span>Ипотечных полисов на агента / мес</span><b>{ipoteka}</b></div>
                            <input type="range" min="0" max="15" value={ipoteka} onChange={e => setIpoteka(+e.target.value)} style={{ width: '100%', accentColor: 'var(--ab-color-brand-500)' }} />
                        </div>
                    </div>
                    <div className="calc-result">
                        <div className="cr-k">Ваш доход за 12 месяцев</div>
                        <div className="cr-big">{money(Math.round(yearTotal))}</div>
                        <div className="cr-per">≈ {money(Math.round(monthTotal))} в месяц</div>
                        <div className="cr-split">
                            <div className="cr-line"><span>ОСАГО · 100 ₽/полис</span><span>{money(Math.round(monthOsago * 12))}</span></div>
                            <div className="cr-line"><span>Ипотека · 10% КВ</span><span>{money(Math.round(monthIpo * 12))}</span></div>
                        </div>
                    </div>
                </div>
            </div>

            {/* INVITEES — единый список (только для авторизованных; гостю списков нет) */}
            {!IS_GUEST && (
                <div className="section" id="invitees">
                    <div className="section-head">
                        <h2 className="section-title">Мои приглашённые{IS_ADMIN ? ' — все агенты' : ''}</h2>
                        <p className="section-sub">{IS_ADMIN ? 'Режим администратора: показаны приглашения всех агентов' : 'Сверху — принятые рефералы, ниже — отправленные приглашения, которые ещё ждут регистрации'}</p>
                    </div>
                    <div className="inv-toolbar">
                        <span className="inv-total"><span className="sig">Σ</span> Суммарный доход <b>{money(totalEarned)}</b></span>
                    </div>
                    <div className="table-card">
                        {rows.length === 0 ? (
                            <div className="empty">
                                <div className="art"><I.invite width="40" height="40" /></div>
                                <h4>Пока никого нет</h4>
                                <p>Отправьте приглашение по email или скопируйте ссылку и поделитесь ей. Как только коллега зарегистрируется, он поднимется наверх списка.</p>
                                <button className="btn" onClick={() => document.getElementById('mylink')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}><I.arrowR width="16" height="16" /> Показать мою ссылку</button>
                            </div>
                        ) : (
                            <Table
                                rowKey="key"
                                columns={columns}
                                dataSource={rows}
                                rowClassName={(r: Row) => r.kind === 'sent' ? 'row-pending' : ''}
                                scroll={{ x: 720 }}
                                pagination={{ pageSize: 12, showSizeChanger: false, showTotal: (t, r) => `${r[0]}–${r[1]} из ${t}` }}
                            />
                        )}
                    </div>
                </div>
            )}

            {/* АДМИН: все продажи и все начисления (только администратору) */}
            {IS_ADMIN && (
                <div className="section" id="admin-sales">
                    <div className="section-head">
                        <h2 className="section-title">Все продажи <span className="section-sub" style={{ display: 'inline' }}>(invite.sale · админ)</span></h2>
                        <div className="inv-datefilter">
                            <RangePicker value={salesRange} onChange={setSalesRange} format="DD.MM.YYYY" allowEmpty={[true, true]} placeholder={['Дата с', 'по']} />
                        </div>
                    </div>
                    <div className="table-card">
                        <Table
                            rowKey="id"
                            size="small"
                            dataSource={inRange(initial.adminSales || [], salesRange, 'soldAt')}
                            scroll={{ x: 900 }}
                            pagination={{ pageSize: 15, showSizeChanger: false, showTotal: (t) => `Всего: ${t}` }}
                            columns={[
                                { title: 'ID', dataIndex: 'id', width: 70 },
                                { title: 'Пригласивший', dataIndex: 'referrer' },
                                { title: 'Продавец', dataIndex: 'invitee' },
                                { title: 'Продукт', dataIndex: 'product' },
                                { title: 'Полис/договор', dataIndex: 'sourceId' },
                                { title: 'Премия', dataIndex: 'sale', align: 'right', render: (v: number) => money(v) },
                                { title: 'КВ (база)', dataIndex: 'commission', align: 'right', render: (v: number) => money(v) },
                                { title: 'Продано', dataIndex: 'soldAt' },
                                { title: 'Начислено?', dataIndex: 'accrued', align: 'center', render: (v: boolean) => v ? '✓' : '—' },
                            ]}
                        />
                    </div>
                </div>
            )}
            {IS_ADMIN && (
                <div className="section" id="admin-accruals">
                    <div className="section-head">
                        <h2 className="section-title">Все начисления <span className="section-sub" style={{ display: 'inline' }}>(invite.accrual · админ)</span></h2>
                        <div className="inv-datefilter">
                            <RangePicker value={accrualsRange} onChange={setAccrualsRange} format="DD.MM.YYYY" allowEmpty={[true, true]} placeholder={['Дата с', 'по']} />
                        </div>
                    </div>
                    <div className="table-card">
                        <Table
                            rowKey="id"
                            size="small"
                            dataSource={inRange(initial.adminAccruals || [], accrualsRange, 'createdAt')}
                            scroll={{ x: 900 }}
                            pagination={{ pageSize: 15, showSizeChanger: false, showTotal: (t) => `Всего: ${t}` }}
                            columns={[
                                { title: 'ID', dataIndex: 'id', width: 70 },
                                { title: 'Пригласивший', dataIndex: 'referrer' },
                                { title: 'Продавец', dataIndex: 'invitee' },
                                { title: 'Продукт', dataIndex: 'product' },
                                { title: 'Полис/договор', dataIndex: 'sourceId' },
                                { title: 'База', dataIndex: 'base', align: 'right', render: (v: number) => money(v) },
                                { title: 'Вознагр.', dataIndex: 'reward', align: 'right', render: (v: number) => money(v) },
                                {
                                    title: 'Статус', dataIndex: 'status',
                                    render: (v: string, r: AdminAccrual) => {
                                        const tone = v === 'paid' ? 'green' : v === 'approved' ? 'blue' : v === 'held' ? 'red' : 'muted';
                                        const c = chipTone(tone);
                                        return <span className="st-chip" style={{ background: c.bg, color: c.fg }} title={r.holdReason || ''}><span className="dot" style={{ background: c.dot }} />{v}{r.holdReason ? ` · ${r.holdReason}` : ''}</span>;
                                    },
                                },
                                { title: 'Дата', dataIndex: 'createdAt' },
                            ]}
                        />
                    </div>
                </div>
            )}

            {/* FAQ */}
            <div className="section">
                <div className="section-head">
                    <h2 className="section-title">Частые вопросы</h2>
                </div>
                <div className="card">
                    <Faq />
                </div>
            </div>

        </div>
    );
}
